import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryResultsStore, useSourcesStore } from '@/stores';
import {
    execute,
    executeContinue,
    type QueryParams,
    type StreamMeta,
} from './useQuery';
import { useQueryUrlSync } from './useQueryUrlSync';
import { useRetentionStartDate } from './useRetentionStartDate';
import { useSortFromUrl } from './useSortFromUrl';
import { toast } from '@/components';
import { sortEvents, SortOption } from '@/utils/query-filters';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    buildQueryParamsFromUrlState,
    decodeSourceIds,
    deserializeFromUrlParams,
    URL_PARAMS,
} from '@/utils/query-url-params';

export function useQueryExecution() {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const {
        events,
        eventPageMap,
        currentPage,
        addEvent,
        clearEvents,
        incrementPage,
        setLastQueryParams,
        lastExecutedKey,
        setLastExecutedKey,
        hasMore,
        setHasMore,
        cursor,
        setCursor,
        queryLoading,
        setAnalyzedQueries,
        setVehicleResults,
        setPersonResults,
        setQuerySummary,
        setQuerySuggestions,
        setSimilarResults,
        setLoadMoreSummary,
    } = useQueryResultsStore();
    const [sortOption] = useSortFromUrl(SortOption.CONFIDENCE);
    const [searchParams] = useSearchParams();
    const retentionStart = useRetentionStartDate();
    const selectedFolders = useSourcesStore((state) => state.selectedFolders);
    const query = searchParams.get('q');
    const attachmentParam = searchParams.get('attachment');
    const searchTimestamp = searchParams.get('_t');
    const { isInitialized } = useQueryUrlSync();
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const createMetaHandler = useCallback(
        (setSummary: typeof setQuerySummary) => (meta: StreamMeta) => {
            setCursor(meta.cursor ?? null);
            setHasMore(!!meta.cursor);
            if (meta.summary) setSummary(meta.summary);
        },
        [setHasMore, setCursor],
    );

    const executeQuery = useCallback(
        async (queryParams: QueryParams) => {
            setLastQueryParams(queryParams);
            clearEvents();

            try {
                const results = await execute(queryParams, {
                    onEventReceived: addEvent,
                    onMeta: createMetaHandler(setQuerySummary),
                    onAnalyzedQueries: setAnalyzedQueries,
                    onVehicleResults: setVehicleResults,
                    onPersonResults: setPersonResults,
                    onSuggestion: setQuerySuggestions,
                    onSimilarResults: setSimilarResults,
                    onError: (code) =>
                        toast.error(
                            t(
                                `streamErrors.${code}`,
                                t('streamErrors.unknown'),
                            ),
                            { duration: 8000 },
                        ),
                });
                if (results.length === 0) {
                    setHasMore(false);
                }
            } catch (error) {
                console.error('Query execution failed:', error);
                toast.error(
                    error instanceof Error && error.message
                        ? error.message
                        : 'Query execution failed. Please try again.',
                    { duration: 8000 },
                );
            }
        },
        [
            addEvent,
            createMetaHandler,
            setQuerySummary,
            setAnalyzedQueries,
            setVehicleResults,
            setPersonResults,
            setQuerySuggestions,
            setSimilarResults,
            setLastQueryParams,
            clearEvents,
            setHasMore,
            t,
        ],
    );

    const loadMore = useCallback(async () => {
        if (!cursor || isLoadingMore || !hasMore || queryLoading) return;

        setIsLoadingMore(true);
        incrementPage();
        setLoadMoreSummary(null);

        try {
            const results = await executeContinue(cursor, 10, {
                onEventReceived: addEvent,
                onMeta: createMetaHandler(setLoadMoreSummary),
                onAnalyzedQueries: setAnalyzedQueries,
                onError: (code) =>
                    toast.error(
                        t(`streamErrors.${code}`, t('streamErrors.unknown')),
                        { duration: 8000 },
                    ),
            });

            if (results.length === 0) {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Failed to load more results:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [
        cursor,
        isLoadingMore,
        hasMore,
        queryLoading,
        addEvent,
        createMetaHandler,
        setLoadMoreSummary,
        setAnalyzedQueries,
        incrementPage,
        setHasMore,
        t,
    ]);

    useEffect(() => {
        const hasQuery = query && query.trim();
        const hasAttachment = !!attachmentParam;
        if (!isInitialized || !(hasQuery || hasAttachment)) return;

        const urlFolderIds = decodeSourceIds(
            searchParams.get(URL_PARAMS.SOURCE_FOLDERS) ?? '',
        );
        if (urlFolderIds.some((folderId) => !selectedFolders.has(folderId))) {
            return;
        }

        const queryKey = [
            query,
            attachmentParam,
            searchTimestamp,
            searchParams.get(URL_PARAMS.CONFIDENCE),
            searchParams.get(URL_PARAMS.SOURCES),
            searchParams.get(URL_PARAMS.SOURCE_FOLDERS),
            searchParams.get(URL_PARAMS.EXCLUDED),
            searchParams.get(URL_PARAMS.DATE_TIME),
            searchParams.get(URL_PARAMS.OBJECT_TYPES),
        ].join('|');

        if (queryKey !== lastExecutedKey) {
            setLastExecutedKey(queryKey);

            const urlState = deserializeFromUrlParams(
                searchParams,
                retentionStart,
            );
            const { demo } = useQueryResultsStore.getState();
            const { sources: allSources } = useSourcesStore.getState();
            const queryParams = buildQueryParamsFromUrlState(
                urlState,
                demo,
                allSources,
                selectedFolders,
            );
            if (queryParams) {
                executeQuery(queryParams);
            }
        }
    }, [
        isInitialized,
        searchParams,
        query,
        attachmentParam,
        searchTimestamp,
        lastExecutedKey,
        setLastExecutedKey,
        executeQuery,
        selectedFolders,
        retentionStart,
    ]);

    const displayedEvents = useMemo(() => {
        return sortEvents(events, sortOption, eventPageMap);
    }, [events, sortOption, eventPageMap]);

    return {
        loadMore,
        isLoading: queryLoading,
        isLoadingMore,
        hasMore,
        query,
        events,
        displayedEvents,
        eventPageMap,
        currentPage,
    };
}
